import {
  type CreateForwardDraftInputDto,
  type CreateInboxForwardDraftMutation,
  type FetchResult,
  type MutationResult,
  useCreateInboxForwardDraftMutation,
} from "@qw/graphql";
import { useToast } from "@qw/ui";
import { useCallback } from "react";

import { type OpenDraftOptions, useDraftsCompose } from "./use-drafts-compose";

export type UseCreateForwardDraftReturn = [
  (
    input: CreateForwardDraftInputDto,
    options?: OpenDraftOptions
  ) => Promise<FetchResult<CreateInboxForwardDraftMutation> | undefined>,
  MutationResult<CreateInboxForwardDraftMutation>,
];

export const useCreateForwardDraft = (): UseCreateForwardDraftReturn => {
  const { toast } = useToast();
  const { openDraft } = useDraftsCompose();
  const [createForwardDraftMutation, result] = useCreateInboxForwardDraftMutation();

  const createForwardDraft = useCallback(
    (input: CreateForwardDraftInputDto, options?: OpenDraftOptions) =>
      createForwardDraftMutation({
        variables: { input },
        onCompleted: (data) => {
          if (!data?.createInboxForwardDraft) {
            throw new Error("Failed to create forward draft");
          }
          const openOptions: OpenDraftOptions = {
            sourceConversationId: options?.sourceConversationId ?? input.conversationId,
          };

          if (options?.placement) {
            openOptions.placement = options.placement;
          }

          openDraft(data.createInboxForwardDraft.id, openOptions);
        },
        onError: (error) => {
          toast({
            title: "Error creating forward draft",
            description: error instanceof Error ? error.message : "An unexpected error occurred",
            variant: "destructive",
          });
        },
        refetchQueries: ["InboxDrafts", "InboxConversations", "InboxDraftsCount"],
      }),
    [createForwardDraftMutation, toast, openDraft]
  );

  return [createForwardDraft, result];
};
